import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '../../../components/ui/Button';
import PanelCard from '../../performance-operations/components/PanelCard';
import { KpiSub, KpiValue } from '../../performance-operations/styles';
import { usePanel } from '../../performance-operations/config';
import {
  fetchM09, fetchSummary, formatNumber, hasValue, isSuppressed, metricOf,
} from '../config';

// E04 kartları (analiz §7 üst alan + ek kartlar): M09 ağırlıklı gerçekleşme (ortak metrik ucu — küçük grup gizlemeli),
// M10 ölçülebilir hedef kapsaması, M11 tamamlanan, M12 geciken, M13 riskli, M14 onaylı revizyon, M61 stratejik bağlantı,
// M62 güncelleme kapsaması. Her kartta değer, pay/payda ve tanım; payda 0 → "Veri Yok". Tıklanabilir kartlar listeye
// (aynı yüklemle — kart sayısı = liste) ya da Hedef Bağlantıları sekmesine götürür.
export const DEFAULT_CODES = ['M09', 'M10', 'M11', 'M12', 'M13', 'M14', 'M61', 'M62'];

function Card({ code, metric, loading, error, onRetry, action }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  let value;
  if (isSuppressed(metric)) value = t('performance.goalAnalytics.kpi.suppressed');
  else if (!hasValue(metric)) value = t('performance.goalAnalytics.kpi.noData');
  else value = `${formatNumber(metric.value, lang)}${metric.unit === 'count' ? '' : '%'}`;
  // M09 ortalamadır (pay = puan toplamı) — oran satırı yerine örneklem/kapsam gösterilir.
  const ratio = code !== 'M09' && hasValue(metric) && metric.unit !== 'count' && metric.numerator != null && metric.denominator != null
    ? t('performance.goalAnalytics.kpi.ratio', { num: formatNumber(metric.numerator, lang, 0), den: formatNumber(metric.denominator, lang, 0) })
    : null;
  const sampleText = code === 'M09' && hasValue(metric) && metric.sampleN != null && metric.coverage != null
    ? t('performance.goalAnalytics.kpi.sample', { n: formatNumber(metric.sampleN, lang, 0), coverage: formatNumber(metric.coverage, lang) })
    : null;
  return (
    <PanelCard
      panelKey={`kpi-${code}`}
      title={t(`performance.goalAnalytics.kpi.${code}`)}
      span={3}
      kpi
      compact
      loading={loading}
      error={error}
      onRetry={onRetry}
      actions={action}
    >
      <KpiValue data-kpi={code}>{value}</KpiValue>
      {ratio || sampleText ? <KpiSub>{ratio || sampleText}</KpiSub> : null}
      <KpiSub>{t(`performance.goalAnalytics.kpi.${code}Hint`)}</KpiSub>
    </PanelCard>
  );
}

export default function GoalKpiCards({
  filters, goalFilters, onMeta, onDrill, onOpenLinks, codes = DEFAULT_CODES, refreshKey,
}) {
  const { t } = useTranslation();
  const key = JSON.stringify([filters, goalFilters]);
  const needsSummary = codes.some((c) => c !== 'M09');
  const summary = usePanel(() => fetchSummary(filters, goalFilters), [key, refreshKey], !!filters.periodId && needsSummary);
  const m09 = usePanel(() => fetchM09(filters), [JSON.stringify(filters)], !!filters.periodId && codes.includes('M09'));

  useEffect(() => {
    if (summary.data) onMeta?.('goalSummary', { calculatedAt: summary.data.calculatedAt, stale: false, personScope: summary.data.personScope });
  }, [summary.data, onMeta]);
  useEffect(() => {
    if (m09.data) onMeta?.('goalM09', { calculatedAt: m09.data.calculatedAt, stale: !!m09.data.stale });
  }, [m09.data, onMeta]);

  const drill = (code) => {
    const show = (patch) => (
      <Button variant="ghost" onClick={() => onDrill?.((f) => ({ ...f, ...patch }))} data-drill={code}>
        {t('performance.goalAnalytics.kpi.showList')}
      </Button>
    );
    switch (code) {
      case 'M12': return show({ overdue: true });
      case 'M13': return show({ risks: ['behindPlan', 'staleEvidence'] });
      // M62 paydası onaylı hedefler; güncellenmemişler = onaylı + kanıtı güncel olmayan.
      case 'M62': return show({ statuses: ['approved'], evidence: 'stale' });
      case 'M61': return onOpenLinks ? (
        <Button variant="ghost" onClick={onOpenLinks} data-drill={code}>{t('performance.goalAnalytics.kpi.showTree')}</Button>
      ) : null;
      default: return null;
    }
  };

  return (
    <>
      {codes.map((code) => (code === 'M09' ? (
        <Card key={code} code={code} metric={m09.data} loading={m09.loading} error={m09.error} onRetry={m09.retry} />
      ) : (
        <Card
          key={code}
          code={code}
          metric={metricOf(summary.data?.metrics, code)}
          loading={summary.loading}
          error={summary.error}
          onRetry={summary.retry}
          action={drill(code)}
        />
      )))}
    </>
  );
}
